import db from "../db/database";
import { getCurrentDateTimeISO } from "../utils/dateUtils";
import { markForSync } from "./syncService";
import { getAllInvoices } from "./invoiceService";
import formaDePagoService from "./formaDePagoService";
import dayjs from "dayjs";

// Funciones para gestionar pagos de facturas

export const addInvoicePaymentLine = async (paymentLine) => {
	try {
		// El Dto_Amount viene ya calculado desde el formulario
		const discountPercentage = paymentLine.Dto || 0;
		const amount = paymentLine.Amount || 0;
		const discountAmount = paymentLine.Dto_Amount || 0;

		// Crear una línea de pago en la base de datos local
		const id = await db.invoicePaymentLines.add({
			invoiceId: paymentLine.invoiceId,
			Document_Type: paymentLine.Document_Type || "Invoice",
			Document_No_: paymentLine.Document_No_,
			Line_No: paymentLine.Line_No,
			Payment_Method: paymentLine.Payment_Method,
			Amount: amount,
			Dto: discountPercentage,
			Dto_Amount: parseFloat(discountAmount.toFixed(2)),
			pendingSync: 1,
			lastModified: new Date(),
			deleted: false,
		});

		return {
			id,
			...paymentLine,
			Dto_Amount: parseFloat(discountAmount.toFixed(2)),
		};
	} catch (error) {
		console.error("Error al crear línea de pago:", error);
		throw error;
	}
};

export const getInvoicePaymentLines = async (invoiceId) => {
	try {
		return await db.invoicePaymentLines
			.where("invoiceId")
			.equals(invoiceId)
			.and((line) => !line.deleted)
			.toArray();
	} catch (error) {
		console.error(
			`Error al obtener líneas de pago para factura ${invoiceId}:`,
			error
		);
		throw error;
	}
};

export const updateInvoicePaymentLine = async (id, updatedLine) => {
	try {
		// Obtener la línea actual
		const existingLine = await db.invoicePaymentLines.get(id);
		if (!existingLine) {
			throw new Error("La línea de pago no existe");
		}

		const now = getCurrentDateTimeISO();

		// El Dto_Amount viene ya calculado desde el formulario
		const discountAmount = updatedLine.Dto_Amount || 0;

		const lineToUpdate = {
			...updatedLine,
			Dto_Amount: parseFloat(discountAmount.toFixed(2)),
			pendingSync: 1,
			lastModified: now,
		};

		await db.invoicePaymentLines.update(id, lineToUpdate);

		// Marcar como pendiente de sincronización
		await markForSync("invoicePaymentLines", id);

		return { ...lineToUpdate, id };
	} catch (error) {
		console.error(`Error al actualizar línea de pago con ID ${id}:`, error);
		throw error;
	}
};

export const deleteInvoicePaymentLine = async (id) => {
	try {
		// Obtener la línea actual
		const existingLine = await db.invoicePaymentLines.get(id);
		if (!existingLine) {
			throw new Error("La línea de pago no existe");
		}

		const now = getCurrentDateTimeISO();

		await db.invoicePaymentLines.update(id, {
			deleted: true,
			pendingSync: 1,
			lastModified: now,
		});

		// Marcar como pendiente de sincronización
		await markForSync("invoicePaymentLines", id);

		return true;
	} catch (error) {
		console.error(`Error al eliminar línea de pago con ID ${id}:`, error);
		throw error;
	}
};

// Obtener todas las líneas de pago de facturas por fecha específica
export const getPaymentLinesByDate = async (date) => {
	try {
		// Formatear la fecha para comparación
		const targetDate = dayjs(date).format("YYYY-MM-DD");

		// Obtener todas las facturas
		const invoices = await getAllInvoices();

		// Filtrar facturas por fecha - INCLUIR TANTO FACTURAS COMO NOTAS DE CRÉDITO
		const invoicesOnDate = invoices.filter((invoice) => {
			const invoiceDate = dayjs(invoice.Posting_Date).format("YYYY-MM-DD");
			// Incluir tanto facturas normales como notas de crédito
			return (
				invoiceDate === targetDate &&
				(invoice.Document_Type === "Invoice" ||
					invoice.Document_Type === "Credit Memo")
			);
		});

		// Si no hay facturas para esa fecha, retornar un array vacío
		if (invoicesOnDate.length === 0) {
			return [];
		}

		// Obtener los IDs de las facturas
		const invoiceIds = invoicesOnDate.map((invoice) => invoice.id);

		// Obtener todas las líneas de pago que pertenezcan a esas facturas
		const allPaymentLines = await db.invoicePaymentLines
			.filter((line) => !line.deleted && invoiceIds.includes(line.invoiceId))
			.toArray();

		// Obtener todas las formas de pago para enriquecer con el tipo de pago
		const formasDePago = await formaDePagoService.getAll();

		// Enriquecer las líneas de pago con información de la factura y tipo de pago
		const enrichedPaymentLines = allPaymentLines.map((line) => {
			const relatedInvoice = invoicesOnDate.find(
				(inv) => inv.id === line.invoiceId
			);

			// Buscar la forma de pago para obtener el tipo
			const formaPago = formasDePago.find(
				(fp) => fp.Code === line.Payment_Method
			);

			// Si es una nota de crédito, el importe debe ser negativo para reflejar la devolución
			const adjustedAmount =
				relatedInvoice?.Document_Type === "Credit Memo"
					? -Math.abs(line.Amount || 0)
					: line.Amount || 0;

			return {
				...line,
				Amount: adjustedAmount,
				invoiceNo: relatedInvoice?.No || "Desconocido",
				clientName: relatedInvoice?.Bill_to_Name || "Cliente desconocido",
				documentType: relatedInvoice?.Document_Type || "Unknown",
				Payment_Method_Code: line.Payment_Method,
				Tipo_Pago: formaPago?.Tipo_Pago || "Efectivo", // Valor por defecto
			};
		});

		return enrichedPaymentLines;
	} catch (error) {
		console.error(
			`Error al obtener líneas de pago para la fecha ${date}:`,
			error
		);
		throw error;
	}
};

// Obtener todas las líneas de pago de facturas en un rango de fecha y hora específico
// (desde apertura de caja hasta momento del cierre)
export const getPaymentLinesByDateTimeRange = async (
	aperturaDateTime,
	cierreDateTime = null
) => {
	try {
		// Si no se proporciona fecha de cierre, usar la fecha y hora actual
		const endDateTime = cierreDateTime || new Date();

		// Convertir fechas a objetos dayjs para comparación
		const startDateTime = dayjs(aperturaDateTime);
		const endDateTimeFormatted = dayjs(endDateTime);

		console.log(
			`🔍 Buscando facturas desde: ${startDateTime.format(
				"YYYY-MM-DD HH:mm:ss"
			)} hasta: ${endDateTimeFormatted.format("YYYY-MM-DD HH:mm:ss")}`
		);

		// Obtener todas las facturas
		const invoices = await getAllInvoices();

		// Filtrar facturas por rango de fecha y hora usando lastModified
		const invoicesInRange = invoices.filter((invoice) => {
			// Usar lastModified para determinar cuándo se creó/modificó la factura
			if (!invoice.lastModified) {
				return false;
			}

			const invoiceDateTime = dayjs(invoice.lastModified);

			// La factura debe estar dentro del rango de tiempo
			const isInRange =
				invoiceDateTime.isAfter(startDateTime) &&
				invoiceDateTime.isBefore(endDateTimeFormatted);

			// Incluir tanto facturas normales como notas de crédito
			const isValidType =
				invoice.Document_Type === "Invoice" ||
				invoice.Document_Type === "Credit Memo";

			// No incluir borradores
			const isNotDraft = !invoice.isDraft;

			if (isInRange && isValidType && isNotDraft) {
				console.log(
					`✅ Factura incluida: ${
						invoice.No || invoice.id
					} - ${invoiceDateTime.format("YYYY-MM-DD HH:mm:ss")}`
				);
			}

			return isInRange && isValidType && isNotDraft;
		});

		console.log(
			`📊 Facturas encontradas en el rango: ${invoicesInRange.length}`
		);

		// Si no hay facturas para ese rango, retornar un array vacío
		if (invoicesInRange.length === 0) {
			return [];
		}

		// Obtener los IDs de las facturas
		const invoiceIds = invoicesInRange.map((invoice) => invoice.id);

		// Obtener todas las líneas de pago que pertenezcan a esas facturas
		const allPaymentLines = await db.invoicePaymentLines
			.filter((line) => !line.deleted && invoiceIds.includes(line.invoiceId))
			.toArray();

		console.log(`💳 Líneas de pago encontradas: ${allPaymentLines.length}`);

		// Obtener todas las formas de pago para enriquecer con el tipo de pago
		const formasDePago = await formaDePagoService.getAll();

		// Enriquecer las líneas de pago con información de la factura y tipo de pago
		const enrichedPaymentLines = allPaymentLines.map((line) => {
			const relatedInvoice = invoicesInRange.find(
				(inv) => inv.id === line.invoiceId
			);

			// Buscar la forma de pago para obtener el tipo
			const formaPago = formasDePago.find(
				(fp) => fp.Code === line.Payment_Method
			);

			// Si es una nota de crédito, el importe debe ser negativo para reflejar la devolución
			const adjustedAmount =
				relatedInvoice?.Document_Type === "Credit Memo"
					? -Math.abs(line.Amount || 0)
					: line.Amount || 0;

			return {
				...line,
				Amount: adjustedAmount,
				invoiceNo: relatedInvoice?.No || "Desconocido",
				clientName: relatedInvoice?.Bill_to_Name || "Cliente desconocido",
				documentType: relatedInvoice?.Document_Type || "Unknown",
				Payment_Method_Code: line.Payment_Method, // Agregar código de método de pago
				Tipo_Pago: formaPago?.Tipo_Pago || "Efectivo", // Valor por defecto
			};
		});

		const totalAmount = enrichedPaymentLines.reduce(
			(sum, line) => sum + (line.Amount || 0),
			0
		);
		console.log(`💰 Total de pagos en el rango: $${totalAmount.toFixed(2)}`);

		return enrichedPaymentLines;
	} catch (error) {
		console.error(
			`Error al obtener líneas de pago para el rango de fechas:`,
			error
		);
		throw error;
	}
};
